import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import axios from '../../utils/axiosDash';

const HlsStreamPlayer = ({
  camId,        // Numeric ID (like "130") for HTTP endpoint
  camIp,
  subnetId,
  fullCamId,    // Full ID (like "CAM 10_130") for WebSocket
  autoStart = true,
  width = '100%',
  height = 'auto',
  showControls = true,
  onError,
  onStatusChange,
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);

  // ─── Cleanup ──────────────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.load();
    }
  }, []);

  // ─── HLS player init ──────────────────────────────────────────────────────
  const initPlayer = useCallback(
    (url) => {
      const video = videoRef.current;
      if (!video) return;

      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 5,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          backBufferLength: 90,
          fragLoadingTimeOut: 20000,
          manifestLoadingTimeOut: 10000,
          levelLoadingTimeOut: 10000,
          fragLoadingMaxRetry: 6,
          manifestLoadingMaxRetry: 4,
          levelLoadingMaxRetry: 4,
          fragLoadingRetryDelay: 1000,
          manifestLoadingRetryDelay: 1000,
        });

        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {
            // console.log('Autoplay blocked — user interaction needed');
          });
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS Error:', data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                // console.log('Network error, trying to recover…');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                // console.log('Media error, trying to recover…');
                hls.recoverMediaError();
                break;
              default:
                // console.log('Fatal error, cannot recover');
                setError('Stream playback failed');
                setStatus('error');
                if (onError) onError(new Error('HLS fatal error'));
                break;
            }
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.addEventListener('loadedmetadata', () => {
          video.play();
        });
      } else {
        setError('HLS not supported in this browser');
        setStatus('error');
      }
    },
    [onError],
  );

  // ─── WebSocket keepalive ──────────────────────────────────────────────────
  const initWebSocket = useCallback((id) => {
    const wsUrl = "wss://cognivision-be.citriot.ai/ws";
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        const wsMessage = {
          action: 'start',
          camId: id,  // Use full ID for WebSocket
          options: { transcode: true, fps: 3 }
        };
        ws.send(JSON.stringify(wsMessage));

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const pingMsg = { action: 'ping', camId: id };
            // console.log('🔍 [NEW HLS] Sending ping:', pingMsg);
            ws.send(JSON.stringify(pingMsg));
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log('🔍 [NEW HLS] WebSocket message:', data);
          if (data.type === 'error') {
            console.error('🔍 [NEW HLS] WebSocket error:', data.message);
          }
        } catch {
          console.warn('🔍 [NEW HLS] WebSocket non-JSON message:', event.data);
        }
      };

      ws.onerror = (err) => {
        console.error('🔍 [NEW HLS] WebSocket error:', err);
      };
      
      ws.onclose = () => {
        // console.log('🔍 [NEW HLS] WebSocket closed');
      };
    } catch (err) {
      console.error('🔍 [NEW HLS] Failed to connect WebSocket:', err);
    }
  }, []);

  // ─── Start stream ─────────────────────────────────────────────────────────
  const startStream = useCallback(async () => {
    if (!camId || !subnetId) {
      console.error('🔍 [NEW HLS] Missing camId or subnetId:', { camId, subnetId });
      setError('Missing camId or subnetId');
      setStatus('error');
      return;
    }

    setStatus('starting');
    setError(null);

    try {
      // Use numeric ID in the URL path (like old working code)
      const url = `/hls-stream/start/${subnetId}/${camId}`;
      const payload = {
        ip: camIp,  // HTTP endpoint expects 'ip'
        transcode: true,
        fps: 3,
        bitrate: '256k',
        resolution: '480x270',
      };

      const { data } = await axios.post(url, payload);

      setStreamUrl(data.hlsUrl);
      setStatus('playing');
      initPlayer(data.hlsUrl);
      // Use full ID for WebSocket (if available), otherwise use numeric ID
      initWebSocket(fullCamId || camId);
      if (onStatusChange) onStatusChange('playing', data);
    } catch (err) {
      console.error('🔍 [NEW HLS] Start stream error:', {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      const message = err.response?.data?.message ?? err.message ?? 'Failed to start stream';
      setError(message);
      setStatus('error');
      if (onError) onError(new Error(message));
    }
  }, [camId, subnetId, camIp, fullCamId, initPlayer, initWebSocket, onError, onStatusChange]);

  // ─── Stop stream ──────────────────────────────────────────────────────────
  const stopStream = useCallback(async () => {
    cleanup();
    setStatus('stopped');
    setStreamUrl(null);
    try {
      await axios.post(`/hls-stream/stop/${subnetId}/${camId}`, {});
    } catch (err) {
      console.error('Stop stream error:', err);
    }
    if (onStatusChange) onStatusChange('stopped');
  }, [camId, subnetId, cleanup, onStatusChange]);

  // ─── Auto-start ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (autoStart && camId && subnetId) {
      startStream();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camId, subnetId, autoStart]);

  // ─── Visibility change ────────────────────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        videoRef.current?.pause();
      } else if (status === 'playing') {
        videoRef.current?.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [status]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getStatusColor = () => {
    switch (status) {
      case 'playing':  return '#0f0';
      case 'starting': return '#ff0';
      case 'error':    return '#f00';
      case 'stopped':  return '#888';
      default:         return '#fff';
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ width, height, background: '#000', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
      <div style={{
        position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center',
        gap: 6, zIndex: 10, background: 'rgba(0,0,0,0.7)', padding: '4px 10px',
        borderRadius: 12, fontSize: 12,
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%', background: getStatusColor(),
          animation: status === 'starting' ? 'pulse 1s infinite' : 'none',
        }} />
        <span style={{ color: '#fff', textTransform: 'capitalize' }}>{status}</span>
        {streamUrl && <span style={{ color: '#888', fontSize: 10 }}>{camId}</span>}
      </div>

      <video
        ref={videoRef}
        style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
        controls={showControls}
        muted
        playsInline
      />

      {error && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.8)', color: '#f00', gap: 10,
        }}>
          <div>⚠️ {error}</div>
          <button
            onClick={startStream}
            style={{ padding: '8px 20px', background: '#f00', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {status === 'starting' && !error && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', color: '#ff0',
        }}>
          <div>Loading stream…</div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default HlsStreamPlayer;